import React, { useState } from "react";
import { Link, NavigateFunction } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, ArrowRight } from "lucide-react";
import { toast } from "react-toastify";
import axios from "../../../services/axiosInstance";
import Field from "./Field";

interface LoginFormProps {
  login: (token: string, refreshToken: string) => void;
  navigate: NavigateFunction;
}

const LoginForm: React.FC<LoginFormProps> = ({ login, navigate }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const isValidEmail = (value: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isLoading) return; // ✅ prevent double click

    const cleanEmail = email.trim();
    const cleanPassword = password.trim();

    if (!cleanEmail || !cleanPassword) {
      toast.error("Veuillez remplir tous les champs.");
      return;
    }

    if (!isValidEmail(cleanEmail)) {
      toast.error("Adresse email invalide.");
      return;
    }

    setIsLoading(true);

    try {
      const { data } = await axios.post("/api/login", {
        email: cleanEmail,
        password: cleanPassword,
      });

      if (!data?.token || !data?.refresh_token) {
        throw new Error("Réponse invalide du serveur");
      }

      login(data.token, data.refresh_token);
      toast.success("Connexion réussie !");
      navigate("/sites-web");
    } catch (err: any) {
      const message =
        err?.response?.data?.error ||
        err?.message ||
        "Échec de la connexion. Vérifiez vos identifiants.";

      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleLogin}
      className="flex flex-col gap-4"
      aria-busy={isLoading}
    >
      <Field
        id="email"
        type="email"
        label="Adresse email"
        placeholder="votre@email.com"
        value={email}
        onChange={(v: string) => setEmail(v)}
        icon={<Mail size={14} />}
        required
      />

      <Field
        id="password"
        type={showPassword ? "text" : "password"}
        label="Mot de passe"
        placeholder="••••••••"
        value={password}
        onChange={(v: string) => setPassword(v)}
        icon={<Lock size={14} />}
        required
        extra={
          <button
            type="button"
            onClick={() => setShowPassword((p) => !p)}
            className="text-slate-300 hover:text-slate-500 transition-colors"
            aria-label={
              showPassword
                ? "Masquer le mot de passe"
                : "Afficher le mot de passe"
            }
          >
            {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        }
      />

      <div className="flex justify-end -mt-1">
        <Link
          to="/forgot-password"
          className="text-xs font-semibold text-slate-400 hover:text-[#0F52BA]"
        >
          Mot de passe oublié ?
        </Link>
      </div>

      <button
        type="submit"
        disabled={isLoading}
        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl
        bg-[#0F52BA] text-white text-sm font-bold
        hover:bg-blue-700 active:scale-[0.98]
        disabled:opacity-60 disabled:cursor-not-allowed
        transition-all duration-150 shadow-sm shadow-blue-200 mt-1"
      >
        {isLoading ? (
          <>
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            Connexion…
          </>
        ) : (
          <>
            Se connecter
            <ArrowRight size={15} />
          </>
        )}
      </button>
    </form>
  );
};

export default LoginForm;